import { Mutation, Action } from 'vuex-module-decorators';
import { Resolve } from 'vue-di';
import { InjectModule, InjectVuexModule } from 'vue-di/vuex';

import { EdgePatientEnrollmentApi } from '@trialcard/apigateway.client/edgePatientEnrollmentApi';
import {
	PatientIntakeRequestModel,
	EnterpriseCoordinatorAccountUpsearchRequestModel,
	UpsearchModelEnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
	UpsearchModelEnterpriseCoordinatorAccountServiceModelsAccountUpsearchModel,
	EnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
	EnterpriseCoordinatorSharedAddressUpsearchModel,
	EnterpriseCoordinatorSharedEmailAddressUpsearchModel,
	EnterpriseCoordinatorSharedPhoneUpsearchModel,
	EnterpriseCoordinatorAccountServiceModelsAccountContactUpsearchModel,
	EnterpriseCoordinatorPayorServiceModelsPayorBenefitModel,
	EnterpriseCoordinatorPayorServiceModelsPayorUpsearchModel,
	UpsearchModelEnterpriseCoordinatorPayorServiceModelsPayorUpsearchModel,
	UpsearchModelEnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel,
	EnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel,
	UpsearchModelEnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel,
	EnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel,
	EnterpriseCoordinatorSharedContactUpsearchModel,
	SurveySessionValueModel,
	IntakePatientIntakeDefaultResponseData,
	EnterpriseCoordinatorPrescriptionServiceModelsPrescriptionModel,
} from '@trialcard/apigateway.models';
import * as enums from '@trialcard/enums';

import moment, { Moment } from 'moment';
import { EdgeEnrollmentApi, EdgeIntakeApi } from '@trialcard/apigateway.client';
import gender from '~/assets/gender';

import { Settings } from '~/services/settings';
import EligibilityModule from '~/store/eligibility';
import SurveyModule from '~/store/survey';
import { FullSurveyModelSimple } from '~/models/survey';

/* eslint-disable dot-notation */
@InjectModule({ stateFactory: true }, module)
export default class EnrollmentModule extends InjectVuexModule {
	@Resolve
	public patientEnrollmentApi!: EdgePatientEnrollmentApi;

	@Resolve
	public patientIntakeApi!: EdgeIntakeApi;

	@Resolve
	public enrollmentApi!: EdgeEnrollmentApi;

	@Resolve
	public settings!: Settings;

	@Resolve
	public survey!: SurveyModule;

	@Resolve
	public eligibility!: EligibilityModule;

	alternativeEnrollment: boolean | null = null;

	memberNumber: string | null = null;

	benefitSelectedToString() {
		let selectedString = '';
		selectedString = this.patientBenefitsSelected.priorAuth ? selectedString + 'Prior Authorization/Precertification' : selectedString;
		selectedString =
			this.patientBenefitsSelected.appealsClaimSupport && selectedString === ''
				? selectedString + 'Appeals/Claims Support'
				: this.patientBenefitsSelected.appealsClaimSupport
					? selectedString + ', Appeals/Claims Support'
					: selectedString;
		selectedString =
			this.patientBenefitsSelected.copayProgram && selectedString === ''
				? selectedString + 'Copay Program'
				: this.patientBenefitsSelected.copayProgram
					? selectedString + ', Copay Program'
					: selectedString;
		selectedString =
			this.patientBenefitsSelected.patientAssistantProgram && selectedString === ''
				? selectedString + 'Patient Assistance Program (PAP)'
				: this.patientBenefitsSelected.patientAssistantProgram
					? selectedString + ', Patient Assistance Program (PAP)'
					: selectedString;
		selectedString =
			this.patientBenefitsSelected.nursingSupport && selectedString === ''
				? selectedString + 'Nursing Support'
				: this.patientBenefitsSelected.patientAssistantProgram
					? selectedString + ', Nursing Support'
					: selectedString;
		return selectedString;
	}

	defaultPatientApplicationData = {
		patientFirstName: '' as string,
		patientLastName: '' as string,
		patientMiddleInitial: '' as string,
		patientAddressOne: '' as string,
		patientAddressTwo: '' as string,
		city: '' as string,
		state: '' as string,
		zip: '' as string,
		patientDateOfBirth: null as Moment | string | null,
		gender: null as enums.Gender | null,
		patientHomePhoneNumber: '' as string,
		patientCellPhoneNumber: '' as string,
		patientEmailAddress: '' as string,
		preferredContactMethod: '' as string,
		preferredContactTime: '' as string,
		legalRepresentative: '' as string,
		relationshipToPatient: '' as string,
		legalRepPhoneNumber: '' as string,
		legalRepEmail: '' as string,

		primaryInsuranceName: '' as string,
		primaryInsurancePhone: '' as string,
		primaryInsuranceGroupNumber: '' as string,
		primaryInsuranceId: '' as string,
		primaryInsuranceBIN: '' as string,
		primaryInsurancePCN: '' as string,
		primaryPolicyholderFirstName: '' as string,
		primaryPolicyholderLastName: '' as string,

		secondaryInsuranceName: '' as string,
		secondaryInsurancePhone: '' as string,
		secondaryInsuranceGroupNumber: '' as string,
		secondaryInsuranceId: '' as string,

		prescriptionInsuranceName: '' as string,
		prescriptionInsurancePhone: '' as string,
		prescriptionInsuranceBIN: '' as string,
		prescriptionInsurancePCN: '' as string,
		prescriptionInsuranceGroup: '' as string,
		prescriptionInsuranceID: '' as string,
		prescriptionPolicyholderFirstName: '' as string,
		prescriptionPolicyholderLastName: '' as string,

		physicianFirstName: '' as string,
		physicianLastName: '' as string,
		physicianPhoneNumber: '' as string,
		physicianFaxNumber: '' as string,
		physicianNpi: '' as string,
		physicianDea: '' as string,
		physicianTaxId: '' as string,
		physicianPtan: '' as string,
		guardianFirstName: '' as string,
		guardianLastName: '' as string,

		practiceName: '' as string,
		practiceAddress: '' as string,
		practiceCity: '' as string,
		practiceState: '' as string,
		practiceZip: '' as string,
		practicePhone: '' as string,
		practiceFax: '' as string,
		officeContactName: '' as string,
		officeContactDirectPhone: '' as string,
		officeContactEmail: '' as string,

		infusionSiteName: '' as string,
		infusionAddress: '' as string,
		infusionCity: '' as string,
		infusionState: '' as string,
		infusionZip: '' as string,
		infusionPhone: '' as string,
		infusionFax: '' as string,
		infusionContactName: '' as string,
		infusionSiteContactDirectPhone: '' as string,
		infusionSiteContactEmail: '' as string,

		prescriptionOneStartDate: null as Moment | string | null,
		prescriptionOneOtherDirections: '' as string,
		prescriptionOneQuantity: '' as string,
		prescriptionTwoStartDate: null as Moment | string | null,
		prescriptionTwoOtherDirections: '' as string,
		prescriptionTwoQuantity: '' as string,
		prescriptionTwoRefill: '' as string

		// papPrescription1StartDate: null as Moment | string | null,
		// papPrescription1OtherDirections: '' as string,
		// papPrescription1Quantity: '' as string,
		// papPrescriptionTwoStartDate: null as Moment | string | null,
		// papPrescriptionTwoOtherDirections: '' as string,
		// papPrescriptionTwoQuantity: '' as string,
	};

	benefitsSelected = {
		priorAuth: null as boolean | null,
		appealsClaimSupport: null as boolean | null,
		copayProgram: null as boolean | null,
		patientAssistantProgram: null as boolean | null,
		nursingSupport: null as boolean | null,
	};

	clinicalSurveyInformation = {
		diagnosis: '' as string,
		icd10: '' as string,
		diagnosisDate: null as Moment | string | null,
		patientWeight: '' as string,
		mostRecentTherapies: '' as string,
		priorTherapies: '' as string,
		prescriptionHcpName: '' as string,
		prescriptionHcpSignature: '' as string,
		prescriptionHcpSignatureDate: null as Moment | string | null,

		papDiagnosis: '' as string,
		papIcd10: '' as string,
		papDiagnosisDate: null as Moment | string | null,
		papPatientWeight: '' as string,
		papMostRecentTherapies: '' as string,
		papPriorTherapies: '' as string,
		// papPrescriptionHcpName: '' as string,
		// papPrescriptionHcpSignature: '' as string,
		// papPrescriptionHcpSignatureDate: null as Moment | string | null,
	};

	prescriberAttestation = {
		prescriberName: '' as string,
		prescriberSignature: '' as string,
		attestationDate: null as Moment | string | null,
	};

	patientApplicationData = { ...this.defaultPatientApplicationData };
	patientBenefitsSelected = { ...this.benefitsSelected };
	patientClinicalSurveyInformation = { ...this.clinicalSurveyInformation };
	enrollmentPrescriberAttestation = { ...this.prescriberAttestation };

	@Mutation
	public async updatePatientApplication(newPatientApplication: typeof EnrollmentModule.prototype.patientApplicationData) {
		this.patientApplicationData = { ...this.patientApplicationData, ...newPatientApplication };
	}

	@Mutation
	public async patchPatientApplication(partialPatientApplication: Partial<typeof EnrollmentModule.prototype.patientApplicationData>) {
		this.patientApplicationData = { ...this.patientApplicationData, ...partialPatientApplication };
	}

	@Mutation
	public updateBenefitsSelected(benefitsSelected: typeof EnrollmentModule.prototype.benefitsSelected) {
		debugger;
		this.patientBenefitsSelected = benefitsSelected;
		console.log(this.patientBenefitsSelected)
	}

	@Mutation
	public updateClinicalSurveyInformaton(clinicalSurveyInformation: typeof EnrollmentModule.prototype.clinicalSurveyInformation) {
		this.patientClinicalSurveyInformation = clinicalSurveyInformation;
	}

	@Mutation
	public updatePrescriberAttestation(prescriberAttestation: typeof EnrollmentModule.prototype.prescriberAttestation) {
		this.enrollmentPrescriberAttestation = prescriberAttestation;
	}

	@Mutation
	public setMemberNumber(memberNumber: string | null) {
		this.memberNumber = memberNumber;
	}

	@Action({ rawError: true })
	public clearEnrollment() {
		this.updatePatientApplication({ ...this.defaultPatientApplicationData });
		this.updateBenefitsSelected({ ...this.benefitsSelected });
		this.updateClinicalSurveyInformaton({ ...this.clinicalSurveyInformation });
		this.updatePrescriberAttestation({ ...this.prescriberAttestation });
	}

	public async submitToAPI() {
		debugger;
		try {
			const patient = {
				patientAccountUpsearch: {
					sourceId: parseInt(this.settings.sourceId),
					patientData: {
						canUpdate: true,
						canInsert: true,
						skipSearch: false,
						model: {
							...this.patientApplicationData,
							firstName: this.patientApplicationData.patientFirstName,
							lastName: this.patientApplicationData.patientLastName,
							middleName: this.patientApplicationData.patientMiddleInitial,
							phoneNumbers: [
								{
									phoneType: enums.ContactInfoType.PHONE,
									phonePurpose: enums.ContactInfoPurposeType.MAIN,
									phoneNumber: this.patientApplicationData.patientHomePhoneNumber.replace(/\D/g, ''),
									contactTimePreferenceType: enums.ContactTimePreferenceType.BETWEENTIMES,
									bestTimeToCall: this.patientApplicationData.preferredContactTime
								}
							] as EnterpriseCoordinatorSharedPhoneUpsearchModel[],
							emailAddresses: [
								{
									emailAddressType: 0,
									address: this.patientApplicationData.patientEmailAddress,
								},
							] as EnterpriseCoordinatorSharedEmailAddressUpsearchModel[],
							dateOfBirth: this.patientApplicationData.patientDateOfBirth,
							addresses: [
								{
									addressType: enums.AddressType.MAILINGADDRESS,
									addressOne: this.patientApplicationData.patientAddressOne,
									addressTwo: this.patientApplicationData.patientAddressTwo,
									city: this.patientApplicationData.city,
									state: this.patientApplicationData.state,
									zip: this.patientApplicationData.zip,
								},
							] as EnterpriseCoordinatorSharedAddressUpsearchModel[],
							suffix: '',
							nickname: '',
						} as EnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
					} as UpsearchModelEnterpriseCoordinatorPatientServiceModelsPatientUpsearchModel,
					accountData: {
						canUpdate: true,
						canInsert: true,
						skipSearch: false,
						model: {
							accountType: 1,
							accountStatus: 1,
							contacts:
								this.patientApplicationData.legalRepresentative
									? [
										{
											roleType: enums.RoleType.LegalRepresentative,
											firstName: this.patientApplicationData.legalRepresentative,
											phoneNumber: this.patientApplicationData.legalRepPhoneNumber
										},
									]
									: ([] as EnterpriseCoordinatorAccountServiceModelsAccountContactUpsearchModel[]),
							contactPreferences: [
								{
									contactInfoType: enums.ContactInfoType.PHONE,
									isOptedIn: false,
									isPreferred: this.patientApplicationData.preferredContactMethod === 'homePhone'
								},
								{
									contactInfoType: enums.ContactInfoType.MOBILE,
									isOptedIn: false,
									isPreferred: this.patientApplicationData.preferredContactMethod === 'cellPhone'
								},
								{
									contactInfoType: enums.ContactInfoType.EMAIL,
									isOptedIn: false,
									isPreferred: this.patientApplicationData.preferredContactMethod === 'email'
								},
							]
						},
					} as UpsearchModelEnterpriseCoordinatorAccountServiceModelsAccountUpsearchModel,
					payorData: this.prepPayorModel(),
					prescriberData: [
						{
							canUpdate: false,
							canInsert: true,
							skipSearch: true,
							model: {
								accountRelationshipIsPrimary: true,
								firstName: this.patientApplicationData.physicianFirstName,
								lastName: this.patientApplicationData.physicianLastName,
								phoneNumbers: [
									{
										phoneType: enums.ContactInfoType.PHONE,
										phonePurpose: enums.ContactInfoPurposeType.MAIN,
										phoneNumber: this.patientApplicationData.physicianPhoneNumber.replace(/\D/g, ''),
									},
									{
										phoneType: enums.ContactInfoType.FAX,
										phonePurpose: enums.ContactInfoPurposeType.MAIN,
										phoneNumber: this.patientApplicationData.physicianFaxNumber.replace(/\D/g, ''),
									},
								] as EnterpriseCoordinatorSharedPhoneUpsearchModel[],
								npi: this.patientApplicationData.physicianNpi || undefined,
								prescriberLicenses: this.patientApplicationData.physicianDea
									? [
										{
											licenseType: enums.PrescriberLicenseType.DRUGENFORCEMENTADMINISTRATIONNUMBER,
											licenseNumber: this.patientApplicationData.physicianDea,
										},
									]
									: [],
								taxId: this.patientApplicationData.physicianTaxId || undefined,
								externalIds: this.patientApplicationData.physicianPtan
									? [
										{
											externalIdType: enums.PartyExternalIdType.ProviderTransactionAccessNumber,
											externalId: this.patientApplicationData.physicianPtan,
										},
									]
									: [],
							} as EnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel,
						} as UpsearchModelEnterpriseCoordinatorPrescriberServiceModelsPrescriberUpsearchModel,
					],
					siteData: [
						{
							canUpdate: false,
							canInsert: true,
							skipSearch: true,
							model: {
								accountRelationshipIsPrimary: true,
								siteRoleType: enums.RoleType.PrescriberSite,
								description: this.patientApplicationData.practiceName,
								addresses: this.patientApplicationData.practiceAddress
									? [
										{
											addressType: enums.AddressType.MAILINGADDRESS,
											addressOne: this.patientApplicationData.practiceAddress,
											city: this.patientApplicationData.practiceCity,
											state: this.patientApplicationData.practiceState,
											zip: this.patientApplicationData.practiceZip,
										},
									]
									: [],
								phoneNumbers: [
									{
										phoneNumber: this.patientApplicationData.practiceFax,
										phonePurpose: enums.ContactInfoPurposeType.MAIN,
										phoneType: enums.ContactInfoType.FAX,
									},
									{
										phoneNumber: this.patientApplicationData.practicePhone,
										phonePurpose: enums.ContactInfoPurposeType.MAIN,
										phoneType: enums.ContactInfoType.PHONE,
									},
								],
							} as EnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel,
						},
					] as UpsearchModelEnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel,
					prescriptionData: [] as EnterpriseCoordinatorPrescriptionServiceModelsPrescriptionModel,
					surveySessionData: (await this.createSurveySessions()) as number[],
				} as EnterpriseCoordinatorAccountUpsearchRequestModel,
			} as PatientIntakeRequestModel;

			if (this.patientApplicationData.officeContactName && patient?.patientAccountUpsearch?.siteData[0]?.model) {
				patient.patientAccountUpsearch.siteData[0].model = {
					...patient.patientAccountUpsearch.siteData[0].model,
					contacts: [
						{
							...(patient?.patientAccountUpsearch?.siteData[0]?.model?.contacts ? patient?.patientAccountUpsearch?.siteData[0]?.model?.contacts[0] : {}),
							firstName: this.patientApplicationData.officeContactName,
						} as EnterpriseCoordinatorSharedContactUpsearchModel
					]
				}
			}

			if (this.patientApplicationData.officeContactEmail && patient?.patientAccountUpsearch?.siteData[0]?.model) {
				patient.patientAccountUpsearch.siteData[0].model = {
					...patient.patientAccountUpsearch.siteData[0].model,
					contacts: [
						{
							...(patient?.patientAccountUpsearch?.siteData[0]?.model?.contacts ? patient?.patientAccountUpsearch?.siteData[0]?.model?.contacts[0] : {}),
							emailAddresses: [
								{
									address: this.patientApplicationData.officeContactEmail
								}
							],
						} as EnterpriseCoordinatorSharedContactUpsearchModel
					]
				}
			}

			if (this.patientApplicationData.officeContactDirectPhone && patient?.patientAccountUpsearch?.siteData[0]?.model) {
				patient.patientAccountUpsearch.siteData[0].model = {
					...patient.patientAccountUpsearch.siteData[0].model,
					contacts: [
						{
							...(patient?.patientAccountUpsearch?.siteData[0]?.model?.contacts ? patient?.patientAccountUpsearch?.siteData[0]?.model?.contacts[0] : {}),
							phoneNumbers: [
								{
									phoneType: enums.ContactInfoType.PHONE,
									phonePurpose: enums.ContactInfoPurposeType.WORK,
									phoneNumber: this.patientApplicationData.officeContactDirectPhone,
								}
							]
						} as EnterpriseCoordinatorSharedContactUpsearchModel
					]
				}
			}

			if (this.patientApplicationData.patientCellPhoneNumber &&
				patient.patientAccountUpsearch.patientData &&
				patient.patientAccountUpsearch.patientData.model
			) {
				patient.patientAccountUpsearch.patientData.model.phoneNumbers.push(
					{
						phoneType: enums.ContactInfoType.MOBILE,
						phonePurpose: enums.ContactInfoPurposeType.MAIN,
						phoneNumber: this.patientApplicationData.patientCellPhoneNumber.replace(/\D/g, ''),
						contactTimePreferenceType: enums.ContactTimePreferenceType.BETWEENTIMES,
						bestTimeToCall: this.patientApplicationData.preferredContactTime
					},
				)
			}

			if (
				this.patientApplicationData.prescriptionOneStartDate ||
				this.patientApplicationData.prescriptionOneOtherDirections ||
				this.patientApplicationData.prescriptionOneQuantity
			) {
				patient.patientAccountUpsearch.prescriptionData.push({
					writtenOn: this.patientApplicationData.prescriptionOneStartDate
						? moment(this.patientApplicationData.prescriptionOneStartDate)
						: undefined,
					directions: this.patientApplicationData.prescriptionOneOtherDirections,
					quantity: this.patientApplicationData.prescriptionOneQuantity
						? +this.patientApplicationData.prescriptionOneQuantity
						: undefined,
					refills: this.patientApplicationData.prescriptionOneQuantity
						? 1
						: undefined
				});
			}
			if (
				this.patientApplicationData.prescriptionTwoStartDate ||
				this.patientApplicationData.prescriptionTwoOtherDirections ||
				this.patientApplicationData.prescriptionTwoQuantity
			) {
				patient.patientAccountUpsearch.prescriptionData.push({
					writtenOn: this.patientApplicationData.prescriptionTwoStartDate
						? moment(this.patientApplicationData.prescriptionTwoStartDate)
						: undefined,
					directions: this.patientApplicationData.prescriptionTwoOtherDirections,
					quantity: this.patientApplicationData.prescriptionTwoQuantity
						? +this.patientApplicationData.prescriptionTwoQuantity
						: undefined,
					refills: this.patientApplicationData.prescriptionTwoRefill
						? +this.patientApplicationData.prescriptionTwoRefill
						: undefined
				});
			}
			if (this.patientApplicationData.infusionSiteName) {
				patient.patientAccountUpsearch.siteData.push({
					canUpdate: false,
					canInsert: true,
					skipSearch: true,
					model: {
						accountRelationshipIsPrimary: false,
						siteRoleType: enums.RoleType.PrescriberSite, // siteRoleType: enums.RoleType.InfusionCenter,
						description: this.patientApplicationData.infusionSiteName,
						addresses: this.patientApplicationData.infusionAddress
							? [
								{
									addressType: enums.AddressType.MAILINGADDRESS,
									addressOne: this.patientApplicationData.infusionAddress,
									city: this.patientApplicationData.infusionCity,
									state: this.patientApplicationData.infusionState,
									zip: this.patientApplicationData.infusionZip,
								},
							]
							: [],
						phoneNumbers: [
							{
								phoneNumber: this.patientApplicationData.infusionFax,
								phonePurpose: enums.ContactInfoPurposeType.MAIN,
								phoneType: enums.ContactInfoType.FAX,
							},
							{
								phoneNumber: this.patientApplicationData.infusionPhone,
								phonePurpose: enums.ContactInfoPurposeType.MAIN,
								phoneType: enums.ContactInfoType.PHONE,
							},
						],
					} as EnterpriseCoordinatorSiteServiceModelsSiteUpsearchModel,
				});
			}

			if (this.patientApplicationData.infusionContactName && patient?.patientAccountUpsearch?.siteData[1]?.model) {
				patient.patientAccountUpsearch.siteData[1].model = {
					...patient.patientAccountUpsearch.siteData[1].model,
					contacts: [
						{
							...(patient?.patientAccountUpsearch?.siteData[1]?.model?.contacts ? patient?.patientAccountUpsearch?.siteData[1]?.model?.contacts[0] : {}),
							firstName: this.patientApplicationData.infusionContactName,
						} as EnterpriseCoordinatorSharedContactUpsearchModel
					]
				}
			}

			if (this.patientApplicationData.infusionSiteContactEmail && patient?.patientAccountUpsearch?.siteData[1]?.model) {
				patient.patientAccountUpsearch.siteData[1].model = {
					...patient.patientAccountUpsearch.siteData[1].model,
					contacts: [
						{
							...(patient?.patientAccountUpsearch?.siteData[1]?.model?.contacts ? patient?.patientAccountUpsearch?.siteData[1]?.model?.contacts[0] : {}),
							emailAddresses: [
								{
									address: this.patientApplicationData.infusionSiteContactEmail
								}
							],
						} as EnterpriseCoordinatorSharedContactUpsearchModel
					]
				}
			}

			if (this.patientApplicationData.infusionSiteContactDirectPhone && patient?.patientAccountUpsearch?.siteData[1]?.model) {
				patient.patientAccountUpsearch.siteData[1].model = {
					...patient.patientAccountUpsearch.siteData[1].model,
					contacts: [
						{
							...(patient?.patientAccountUpsearch?.siteData[1]?.model?.contacts ? patient?.patientAccountUpsearch?.siteData[1]?.model?.contacts[0] : {}),
							phoneNumbers: [
								{
									phoneType: enums.ContactInfoType.PHONE,
									phonePurpose: enums.ContactInfoPurposeType.WORK,
									phoneNumber: this.patientApplicationData.infusionSiteContactDirectPhone,
								}
							]
						} as EnterpriseCoordinatorSharedContactUpsearchModel
					]
				}
			}
			const patientIntakeApiResponse = await this.patientIntakeApi.intakePatientIntake(patient);

			return patientIntakeApiResponse.data && patientIntakeApiResponse.data && patientIntakeApiResponse.data.success;
		} catch (error) {
			console.error(error);
			return;
		}
	}

	prepPayorModel() {
		const response: UpsearchModelEnterpriseCoordinatorPayorServiceModelsPayorUpsearchModel[] = [
			{
				canUpdate: true,
				canInsert: true,
				skipSearch: false,
				model: {
					accountRoleType: enums.RoleType.InsuranceProvider,
					accountRelationshipCoverageOrder: 1,
					accountRelationshipIsPrimary: true,
					payorName: this.patientApplicationData.primaryInsuranceName,
					payorType: enums.PayorType.INS,
					payorBenefit: {
						phone: this.patientApplicationData.primaryInsurancePhone.replace(/\D/g, ''),
						groupNumber: this.patientApplicationData.primaryInsuranceGroupNumber,
						policyNumber: this.patientApplicationData.primaryInsuranceId,
						bin: this.patientApplicationData.primaryInsuranceBIN,
						pcn: this.patientApplicationData.primaryInsurancePCN,
						policyHolderFirstName: this.patientApplicationData.primaryPolicyholderFirstName,
						policyHolderLastName: this.patientApplicationData.primaryPolicyholderLastName,
					} as EnterpriseCoordinatorPayorServiceModelsPayorBenefitModel,
				} as EnterpriseCoordinatorPayorServiceModelsPayorUpsearchModel,
			},
		];
		if (
			this.patientApplicationData.secondaryInsuranceName ||
			this.patientApplicationData.secondaryInsuranceId ||
			this.patientApplicationData.secondaryInsuranceGroupNumber ||
			this.patientApplicationData.secondaryInsurancePhone
		) {
			response.push({
				canUpdate: true,
				canInsert: true,
				skipSearch: false,
				model: {
					accountRoleType: enums.RoleType.InsuranceProvider,
					accountRelationshipCoverageOrder: 2,
					accountRelationshipIsPrimary: false,
					payorName: this.patientApplicationData.secondaryInsuranceName,
					payorType: enums.PayorType.INS,
					payorBenefit: {
						phone: this.patientApplicationData.secondaryInsurancePhone
							? this.patientApplicationData.secondaryInsurancePhone.replace(/\D/g, '')
							: undefined,
						groupNumber: this.patientApplicationData.secondaryInsuranceGroupNumber
							? this.patientApplicationData.secondaryInsuranceGroupNumber
							: undefined,
						policyNumber: this.patientApplicationData.secondaryInsuranceId
							? this.patientApplicationData.secondaryInsuranceId
							: undefined,
					},
				} as EnterpriseCoordinatorPayorServiceModelsPayorUpsearchModel,
			});
		}
		if (
			this.patientApplicationData.prescriptionInsuranceName ||
			this.patientApplicationData.prescriptionInsurancePhone ||
			this.patientApplicationData.prescriptionInsuranceBIN ||
			this.patientApplicationData.prescriptionInsurancePCN ||
			this.patientApplicationData.prescriptionInsuranceGroup ||
			this.patientApplicationData.prescriptionInsuranceID
		) {
			response.push({
				canUpdate: true,
				canInsert: true,
				skipSearch: false,
				model: {
					accountRoleType: enums.RoleType.PharmacyBenefitManager,
					accountRelationshipCoverageOrder: 1,
					accountRelationshipIsPrimary: true,
					payorName: this.patientApplicationData.prescriptionInsuranceName,
					payorType: enums.PayorType.PBM,
					payorBenefit: {
						phone: this.patientApplicationData.prescriptionInsurancePhone
							? this.patientApplicationData.prescriptionInsurancePhone.replace(/\D/g, '')
							: undefined,
						bin: this.patientApplicationData.prescriptionInsuranceBIN
							? this.patientApplicationData.prescriptionInsuranceBIN
							: undefined,
						pcn: this.patientApplicationData.prescriptionInsurancePCN
							? this.patientApplicationData.prescriptionInsurancePCN
							: undefined,
						groupNumber: this.patientApplicationData.prescriptionInsuranceGroup
							? this.patientApplicationData.prescriptionInsuranceGroup
							: undefined,
						policyNumber: this.patientApplicationData.prescriptionInsuranceID
							? this.patientApplicationData.prescriptionInsuranceID
							: undefined,
						policyHolderFirstName: this.patientApplicationData.prescriptionPolicyholderFirstName
							? this.patientApplicationData.prescriptionPolicyholderFirstName
							: undefined,
						policyHolderLastName: this.patientApplicationData.prescriptionPolicyholderLastName
							? this.patientApplicationData.prescriptionPolicyholderLastName
							: undefined,
					},
				} as EnterpriseCoordinatorPayorServiceModelsPayorUpsearchModel,
			});
		}
		return response;
	}

	async createSurveySessions() {
		const benefitsSelectionSurveyModel = this.createBenefitSelectionSurveyModel();
		const clinicalSurveyInformationModel = this.createClinicalSurveyModel();
		const enrollmentPrescriberAttestationSurveyModel = this.createPrescriberAttestationSurveyModel();
		const surveySessionsResponse = await this.survey.fetchMultipleSurveySessions([
			benefitsSelectionSurveyModel,
			clinicalSurveyInformationModel,
			enrollmentPrescriberAttestationSurveyModel,
		]);
		return surveySessionsResponse || [];
	}

	createBenefitSelectionSurveyModel() {
		const benefitsSurveyId = this.settings.benefitSelectionSurveyId;
		const benefitsSelectionSurvey = {
			surveyId: +benefitsSurveyId,
			surveyData: [],
		} as FullSurveyModelSimple;
		if (this.patientBenefitsSelected['priorAuth'])
			benefitsSelectionSurvey.surveyData.push({
				questionId: +this.settings.selectBenefitsQuestionId,
				answerId: +this.settings.priorAuthAnswerId,
			} as SurveySessionValueModel);
		if (this.patientBenefitsSelected['appealsClaimSupport'])
			benefitsSelectionSurvey.surveyData.push({
				questionId: +this.settings.selectBenefitsQuestionId,
				answerId: +this.settings.appealsClaimAnswerId,
			} as SurveySessionValueModel);
		if (this.patientBenefitsSelected['copayProgram'])
			benefitsSelectionSurvey.surveyData.push({
				questionId: +this.settings.selectBenefitsQuestionId,
				answerId: +this.settings.copayProgramIdAnswerId,
			} as SurveySessionValueModel);
		if (this.patientBenefitsSelected['patientAssistantProgram'])
			benefitsSelectionSurvey.surveyData.push({
				questionId: +this.settings.selectBenefitsQuestionId,
				answerId: +this.settings.papAnswerId,
			} as SurveySessionValueModel);
		if (this.patientBenefitsSelected['nursingSupport'])
			benefitsSelectionSurvey.surveyData.push({
				questionId: +this.settings.selectBenefitsQuestionId,
				answerId: +this.settings.nursingSupportAnswerId,
			} as SurveySessionValueModel);
		return benefitsSelectionSurvey;
	}

	createClinicalSurveyModel() {
		const clinicalSurveyInfoSurveyId = this.settings.clinicalInformationSurveyId;
		const clinicalInfo = this.patientClinicalSurveyInformation
		const data = {
			surveyId: +clinicalSurveyInfoSurveyId,
			surveyData: [
				{
					questionId: +this.settings.diagnosisQuestionId,
					answerId: +this.settings.diagnosisAnswersId,
					answerValue: clinicalInfo.diagnosis,
				},
				{
					questionId: +this.settings.icd10QuestionId,
					answerId: +this.settings.icd10AnswerId,
					answerValue: clinicalInfo.icd10,
				},
				{
					questionId: +this.settings.diagnosisDateQuestionId,
					answerId: +this.settings.diagnosisDateAnswerId,
					answerValue: clinicalInfo.diagnosisDate,
				},
				{
					questionId: +this.settings.patientWeightQuestionId,
					answerId: +this.settings.patientWeightAnswerId,
					answerValue: clinicalInfo.patientWeight,
				},
				{
					questionId: +this.settings.mostRecentTherapiesQuestionId,
					answerId: +this.settings.mostRecentTherapiesAnswerId,
					answerValue: clinicalInfo.mostRecentTherapies,
				},
				{
					questionId: +this.settings.priorTherapiesQuestionId,
					answerId: +this.settings.priorTherapiesAnswerId,
					answerValue: clinicalInfo.priorTherapies,
				},
				{
					questionId: +this.settings.prescriptionHcpNameQuestionId,
					answerId: +this.settings.prescriptionHcpNameAnswerId,
					answerValue: clinicalInfo.prescriptionHcpName,
				},
				{
					questionId: +this.settings.prescriptionHcpSignatureQuestionId,
					answerId: +this.settings.prescriptionHcpSignatureAnswerId,
					answerValue: clinicalInfo.prescriptionHcpSignature,
				},
				{
					questionId: +this.settings.prescriptionHcpSignatureDateQuestionId,
					answerId: +this.settings.prescriptionHcpSignatureDateAnswerId,
					answerValue: clinicalInfo.prescriptionHcpSignatureDate,
				},
			],
		} as FullSurveyModelSimple;

		if (clinicalInfo.papDiagnosis) {
			data.surveyData.push({
				questionId: +this.settings.papDiagnosisQuestionId,
				answerId: +this.settings.papDiagnosisAnswerId,
				answerValue: clinicalInfo.papDiagnosis
			})
		}

		if (clinicalInfo.papIcd10) {
			data.surveyData.push({
				questionId: +this.settings.papIcd10QuestionId,
				answerId: +this.settings.papIcd10AnswerId,
				answerValue: clinicalInfo.papIcd10
			})
		}

		if (clinicalInfo.papDiagnosisDate) {
			data.surveyData.push({
				questionId: +this.settings.papDiagnosisDateQuestionId,
				answerId: +this.settings.papDiagnosisDateAnswerId,
				answerValue: clinicalInfo.papDiagnosisDate.toString()
			})
		}

		if (clinicalInfo.papPatientWeight) {
			data.surveyData.push({
				questionId: +this.settings.papPatientWeightQuestionId,
				answerId: +this.settings.papPatientWeightAnswerId,
				answerValue: clinicalInfo.papPatientWeight
			})
		}

		if (clinicalInfo.papMostRecentTherapies) {
			data.surveyData.push({
				questionId: +this.settings.papMostRecentTherapiesQuestionId,
				answerId: +this.settings.papMostRecentTherapiesAnswerId,
				answerValue: clinicalInfo.papMostRecentTherapies
			})
		}

		if (clinicalInfo.papPriorTherapies) {
			data.surveyData.push({
				questionId: +this.settings.papPriorTherapiesQuestionId,
				answerId: +this.settings.papPriorTherapiesAnswerId,
				answerValue: clinicalInfo.papPriorTherapies
			})
		}

		return data
	}

	createPrescriberAttestationSurveyModel() {
		const prescriberAttestationSurveyId = this.settings.prescriberAttestationSurveyId;
		return {
			surveyId: +prescriberAttestationSurveyId,
			surveyData: [
				{
					questionId: +this.settings.prescriberNameQuestionId,
					answerId: +this.settings.prescriberNameAnswerId,
					answerValue: this.enrollmentPrescriberAttestation.prescriberName,
				},
				{
					questionId: +this.settings.prescriberSignatureQuestionId,
					answerId: +this.settings.prescriberSignatureAnswerId,
					answerValue: this.enrollmentPrescriberAttestation.prescriberSignature,
				},
				{
					questionId: +this.settings.attestationDateQuestionId,
					answerId: +this.settings.attestationDateAnswerId,
					answerValue: this.enrollmentPrescriberAttestation.attestationDate,
				},
			],
		} as FullSurveyModelSimple;
	}

	prepPdfModel() {
		const patientData = this.patientApplicationData;
		const genderText = gender.find(g => (g.code || '').toLocaleLowerCase() === ((patientData.gender || {}).toString() || '').toLocaleLowerCase());
		const clincialData = this.patientClinicalSurveyInformation;
		const prescriberAttestation = this.enrollmentPrescriberAttestation;

		const strings = [
			[
				{ type: 'title', label: 'Benefits Selected', value: '' },
				{ type: 'info', label: 'Prior Authorization/Precertification:', value: this.patientBenefitsSelected.priorAuth ? 'Yes' : 'No' },
				{ type: 'info', label: 'Appeals/Claims Support:', value: this.patientBenefitsSelected.appealsClaimSupport ? 'Yes' : 'No' },
				{ type: 'info', label: 'Copay Program:', value: this.patientBenefitsSelected.copayProgram ? 'Yes' : 'No' },
				{ type: 'info', label: 'Patient Assistance Program (PAP):', value: this.patientBenefitsSelected.patientAssistantProgram ? 'Yes' : 'No' },
				{ type: 'info', label: 'NursingSupport:', value: this.patientBenefitsSelected.nursingSupport ? 'Yes' : 'No' },
			],
			[
				{ type: 'title', label: 'Patient Information', value: '' },
				{ type: 'info', label: 'Patient First Name:', value: patientData.patientFirstName || '' },
				{ type: 'info', label: 'Patient Last Name:', value: patientData.patientLastName || '' },
				{ type: 'info', label: 'Patient Middle Initial:', value: patientData.patientMiddleInitial || '' },
				{ type: 'info', label: 'Patient Address Line 1:', value: patientData.patientAddressOne || '' },
				{ type: 'info', label: 'Patient Address Line 2:', value: patientData.patientAddressTwo || '' },
				{ type: 'info', label: 'Patient Zip Code:', value: patientData.zip || '' },
				{ type: 'info', label: 'Patient City:', value: patientData.city || '' },
				{ type: 'info', label: 'Patient State:', value: patientData.state || '' },
				{ type: 'info', label: 'Patient Gender:', value: genderText?.name || '' },
				{ type: 'info', label: 'Patient Date of Birth:', value: patientData.patientDateOfBirth?.toString() || '' },
				{ type: 'info', label: 'Patient Email:', value: patientData.patientEmailAddress || '' },
				{ type: 'info', label: 'Patient Home Phone Number:', value: patientData.patientHomePhoneNumber || '' },
				{ type: 'info', label: 'Patient Cell Phone Number:', value: patientData.patientCellPhoneNumber || '' },
				{ type: 'info', label: 'Preferred Method of Contact:', value: patientData.preferredContactMethod || '' },
				{ type: 'info', label: 'Best Time to Contact:', value: patientData.preferredContactTime || '' },
				{ type: 'info', label: 'Legal Representative:', value: patientData.legalRepresentative || '' },
				{ type: 'info', label: 'Relationship to Patient:', value: patientData.relationshipToPatient || '' },
				{ type: 'info', label: 'Legal Representative Phone Number:', value: patientData.legalRepPhoneNumber || '' },
				{ type: 'info', label: 'Legal Representative Email:', value: patientData.legalRepEmail || '' },
			],
			[
				{ type: 'title', label: 'Healthcare Provider Information', value: '' },
				{ type: 'info', label: 'Provider First Name:', value: patientData.physicianFirstName || '' },
				{ type: 'info', label: 'Provider Last Name:', value: patientData.physicianLastName || '' },
				{ type: 'info', label: 'Provider NPI:', value: patientData.physicianNpi || '' },
				{ type: 'info', label: 'Provider DEA:', value: patientData.physicianDea || '' },
				{ type: 'info', label: 'Provider Tax ID:', value: patientData.physicianTaxId || '' },
				{ type: 'info', label: 'Provider PTAN:', value: patientData.physicianPtan || '' },
				{ type: 'info', label: 'Site of Care Name:', value: patientData.practiceName || '' },
				{ type: 'info', label: 'Site of Care Address:', value: patientData.practiceAddress || '' },
				{ type: 'info', label: 'Site of Care City:', value: patientData.practiceCity || '' },
				{ type: 'info', label: 'Site of Care State:', value: patientData.practiceState || '' },
				{ type: 'info', label: 'Site of Care Zip:', value: patientData.practiceZip || '' },
				{ type: 'info', label: 'Site of Care Phone Number:', value: patientData.practicePhone || '' },
				{ type: 'info', label: 'Site of Care Fax Number:', value: patientData.practiceFax || '' },
				{ type: 'info', label: 'Site of Care Contact Name:', value: patientData.officeContactName || '' },
				{ type: 'info', label: 'Site of Care Contact Direct Phone:', value: patientData.officeContactDirectPhone || '' },
				{ type: 'info', label: 'Site of Care Contact Email:', value: patientData.officeContactEmail || '' },
			],
			[
				{ type: 'title', label: 'Infusion/Administration Site Information', value: '' },
				{ type: 'info', label: 'Infusion Site Name:', value: patientData.infusionSiteName || '' },
				{ type: 'info', label: 'Infusion Site Address:', value: patientData.infusionAddress || '' },
				{ type: 'info', label: 'Infusion Site City:', value: patientData.infusionCity || '' },
				{ type: 'info', label: 'Infusion Site State:', value: patientData.infusionState || '' },
				{ type: 'info', label: 'Infusion Site Zip:', value: patientData.infusionZip || '' },
				{ type: 'info', label: 'Infusion Site Phone Number:', value: patientData.infusionPhone || '' },
				{ type: 'info', label: 'Infusion Site Fax Number:', value: patientData.infusionFax || '' },
				{ type: 'info', label: 'Infusion Site Contact Name:', value: patientData.infusionContactName || '' },
				{ type: 'info', label: 'Infusion Site Contact Direct Phone:', value: patientData.infusionSiteContactDirectPhone || '' },
				{ type: 'info', label: 'Infusion Site Contact Email:', value: patientData.infusionSiteContactEmail || '' },
			],

			[
				{ type: 'title', label: 'Insurance Information', value: '' },
				{ type: 'info', label: 'Primary Medical Insurance Payer:', value: patientData.primaryInsuranceName || '' },
				{ type: 'info', label: 'Primary Medical Insurance Phone Number:', value: patientData.primaryInsurancePhone || '' },
				{ type: 'info', label: 'Primary Medical Insurance Subscriber Id:', value: patientData.primaryInsuranceId || '' },
				{ type: 'info', label: 'Primary Medical Insurance Group Number:', value: patientData.primaryInsuranceGroupNumber || '' },
				{ type: 'info', label: 'Primary Medical Insurance BIN:', value: patientData.primaryInsuranceBIN || '' },
				{ type: 'info', label: 'Primary Medical Insurance PCN:', value: patientData.primaryInsurancePCN || '' },
				{ type: 'info', label: 'Primary Medical Insurance Policy Holder First Name:', value: patientData.primaryPolicyholderFirstName || '' },
				{ type: 'info', label: 'Primary Medical Insurance Policy Holder Last Name:', value: patientData.primaryPolicyholderLastName || '' },
				{ type: 'info', label: 'Secondary Medical Insurance Payer:', value: patientData.prescriptionInsuranceName || '' },
				{ type: 'info', label: 'Secondary Medical Insurance Phone Number:', value: patientData.prescriptionInsurancePhone || '' },
				{ type: 'info', label: 'Secondary Medical Insurance Subscriber ID:', value: patientData.prescriptionInsuranceID || '' },
				{ type: 'info', label: 'Secondary Medical Insurance Group Number:', value: patientData.prescriptionInsuranceGroup || '' },
				{ type: 'info', label: 'Secondary Medical Insurance BIN:', value: patientData.prescriptionInsuranceBIN || '' },
				{ type: 'info', label: 'Secondary Medical Insurance PCN:', value: patientData.prescriptionInsurancePCN || '' },
				{ type: 'info', label: 'Secondary Medical Insurance Policy Holder First Name:', value: patientData.prescriptionPolicyholderFirstName || '' },
				{ type: 'info', label: 'Secondary Medical Insurance Policy Holder Last Name:', value: patientData.prescriptionPolicyholderLastName || '' },
			],
			[
				{ type: 'title', label: 'Clinical Information', value: '' },
				{ type: 'info', label: 'Diagnosis:', value: clincialData.diagnosis || '' },
				{ type: 'info', label: 'ICD-10 Code:', value: clincialData.icd10 || '' },
				{ type: 'info', label: 'Diagnosis Date:', value: clincialData.diagnosisDate?.toString() || '' },
				{ type: 'info', label: 'Patient Weight:', value: clincialData.patientWeight ? clincialData.patientWeight + ' Kg' : '' },
				{ type: 'info', label: 'Most Recent Therapies for this Diagnosis:', value: clincialData.mostRecentTherapies || '' },
				{ type: 'info', label: 'Prior Therapies for this Diagnosis:', value: clincialData.priorTherapies || '' },
			],
			[
				{ type: 'title', label: 'Patient Assistance Program (PAP)', value: '' },
				{ type: 'info', label: 'Pap Diagnosis:', value: clincialData.papDiagnosis || '' },
				{ type: 'info', label: 'Pap ICD-10 Code:', value: clincialData.papIcd10 || '' },
				{ type: 'info', label: 'Pap Diagnosis Date:', value: clincialData.papDiagnosisDate?.toString() || '' },
				{ type: 'info', label: 'Pap Patient Weight:', value: clincialData.papPatientWeight ? clincialData.papPatientWeight + ' Kg' : '' },
				{ type: 'info', label: 'Pap Most Recent Therapies for this Diagnosis:', value: clincialData.papMostRecentTherapies || '' },
				{ type: 'info', label: 'Pap Prior Therapies for this Diagnosis:', value: clincialData.papPriorTherapies || '' },
				{ type: 'info', label: 'Prescription 1 Start Date:', value: patientData.prescriptionOneStartDate?.toString() || '' },
				{ type: 'info', label: 'Prescription 1 Other Directions:', value: patientData.prescriptionOneOtherDirections || '' },
				{ type: 'info', label: 'Prescription 1 Quantity:', value: patientData.prescriptionOneQuantity + ' 10mg vial(s)' || '' },
				{ type: 'info', label: 'Prescription 1 Refill:', value: patientData.prescriptionOneQuantity ? '1' : '' },
				{ type: 'info', label: 'Prescription 2 Start Date:', value: patientData.prescriptionTwoStartDate?.toString() || '' },
				{ type: 'info', label: 'Prescription 2 Other Directions:', value: patientData.prescriptionTwoOtherDirections || '' },
				{ type: 'info', label: 'Prescription 2 Quantity:', value: patientData.prescriptionTwoQuantity + ' 10mg vial(s)' || '' },
				{ type: 'info', label: 'Prescription 2 Refills:', value: patientData.prescriptionTwoRefill || '' },
				{ type: 'info', label: 'Healthcare Provider Name:', value: clincialData.prescriptionHcpName || '' },
				{ type: 'info', label: 'Healthcare Provider Signature:', value: clincialData.prescriptionHcpSignature || '' },
				{ type: 'info', label: 'Healthcare Provider Signature Date:', value: clincialData.prescriptionHcpSignatureDate?.toString() || '' },
			],
			[
				{ type: 'title', label: 'Healthcare Provider Certification Attestation', value: '' },
				{ type: 'info', label: 'Healthcare Provider Certification Name:', value: prescriberAttestation.prescriberName || '' },
				{ type: 'info', label: 'Healthcare Provider Certification Signature:', value: prescriberAttestation.prescriberSignature || '' },
				{ type: 'info', label: 'Healthcare Provider Certification Signature Date:', value: prescriberAttestation.attestationDate?.toString() || '' },
			],
		]

		return strings;
	}
}
